import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";

// Helper component for text inputs with auto-save
const TextQuestion = ({ id, question, value, onChange, multiline = false, maxLength, placeholder }) => {
  const [localValue, setLocalValue] = useState(value || "");

  useEffect(() => {
    setLocalValue(value || "");
  }, [value]);

  const handleBlur = () => {
    if (localValue !== value) {
      onChange(localValue);
    }
  };

  const Component = multiline ? Textarea : Input;

  return (
    <div className="question-card" data-testid={`question-${id}`}>
      <label className="question-title block mb-3">{question}</label>
      <Component
        value={localValue}
        onChange={(e) => setLocalValue(e.target.value)}
        onBlur={handleBlur}
        placeholder={placeholder || "Enter your response..."}
        maxLength={maxLength}
        rows={multiline ? 4 : undefined}
        className="w-full"
        data-testid={`input-${id}`}
      />
      {maxLength && (
        <p className="text-xs text-muted-foreground mt-1 text-right">
          {localValue.length}/{maxLength} characters
        </p>
      )}
    </div>
  );
};

// Helper component for single choice questions with "Other" option
const SingleChoiceQuestion = ({ id, question, options, value, onChange }) => {
  const [otherText, setOtherText] = useState("");
  const isOtherSelected = value && value.startsWith("Other:");
  
  useEffect(() => {
    if (isOtherSelected) {
      setOtherText(value.replace("Other: ", ""));
    }
  }, [value, isOtherSelected]);

  const handleOptionChange = (option) => {
    if (option === "Other") {
      onChange(`Other: ${otherText}`);
    } else {
      onChange(option);
    }
  };

  const handleOtherTextChange = (text) => {
    setOtherText(text);
    onChange(`Other: ${text}`);
  };

  return (
    <div className="question-card" data-testid={`question-${id}`}>
      <p className="question-title mb-4">{question}</p>
      <RadioGroup 
        value={isOtherSelected ? "Other" : (value || "")} 
        onValueChange={handleOptionChange}
        className="space-y-3"
      >
        {options.map((option, index) => (
          <div
            key={index}
            className={`radio-item ${(value === option || (option === "Other" && isOtherSelected)) ? 'selected' : ''}`}
            onClick={() => handleOptionChange(option)}
          >
            <RadioGroupItem
              value={option}
              id={`${id}-${index}`}
              className="border-slate-300 text-[#003366]"
            />
            <Label
              htmlFor={`${id}-${index}`}
              className="cursor-pointer flex-1 text-sm"
            >
              {option}
            </Label>
          </div>
        ))}
        {/* Other Option - Always shown as last option */}
        <div className={`flex flex-col gap-2 p-3 rounded-lg border transition-colors ${
          isOtherSelected ? 'border-[#003366] bg-blue-50' : 'border-slate-200'
        }`}>
          <div className="flex items-center gap-3 cursor-pointer" onClick={() => handleOptionChange("Other")}>
            <RadioGroupItem
              value="Other"
              id={`${id}-other`}
              className="border-slate-300 text-[#003366]"
            />
            <Label htmlFor={`${id}-other`} className="cursor-pointer text-sm">
              Other (please specify)
            </Label>
          </div>
          {isOtherSelected && (
            <input
              type="text"
              value={otherText}
              onChange={(e) => handleOtherTextChange(e.target.value)}
              placeholder="Please specify..."
              className="ml-7 mt-2 px-3 py-2 border rounded-md text-sm"
              autoFocus
            />
          )}
        </div>
      </RadioGroup>
    </div>
  );
};

// Multiple choice with checkboxes and "Other" option
const MultiChoiceQuestion = ({ id, question, options, value, onChange, maxSelections }) => {
  const [otherText, setOtherText] = useState("");
  const currentValues = Array.isArray(value) ? value : [];
  const otherValue = currentValues.find(v => v && v.startsWith("Other:"));
  const hasOtherSelected = !!otherValue;

  useEffect(() => {
    if (otherValue) {
      setOtherText(otherValue.replace("Other: ", ""));
    }
  }, [otherValue]);

  const handleToggle = (option) => {
    if (currentValues.includes(option)) {
      onChange(currentValues.filter(v => v !== option));
    } else if (!maxSelections || currentValues.length < maxSelections) {
      onChange([...currentValues, option]);
    }
  };

  const handleOtherToggle = () => {
    if (hasOtherSelected) {
      onChange(currentValues.filter(v => !v.startsWith("Other:")));
      setOtherText("");
    } else if (!maxSelections || currentValues.length < maxSelections) {
      onChange([...currentValues, `Other: ${otherText}`]);
    }
  };

  const handleOtherTextChange = (text) => {
    setOtherText(text);
    const filteredValues = currentValues.filter(v => !v.startsWith("Other:"));
    onChange([...filteredValues, `Other: ${text}`]);
  };

  return (
    <div className="question-card" data-testid={`question-${id}`}>
      <p className="question-title mb-2">{question}</p>
      {maxSelections && (
        <p className="text-xs text-muted-foreground mb-3">
          Select up to {maxSelections} options
        </p>
      )}
      <div className="space-y-2">
        {options.map((option, index) => {
          const isSelected = currentValues.includes(option);
          return (
            <div
              key={index}
              className={`checkbox-item ${isSelected ? 'selected' : ''}`}
              onClick={() => handleToggle(option)}
            >
              <Checkbox checked={isSelected} />
              <Label className="cursor-pointer flex-1 text-sm">{option}</Label>
            </div>
          );
        })}
        {/* Other Option */}
        <div className={`flex flex-col gap-2 p-3 rounded-lg border transition-colors ${
          hasOtherSelected ? 'border-[#003366] bg-blue-50' : 'border-slate-200'
        }`}>
          <div className="flex items-center gap-3 cursor-pointer" onClick={handleOtherToggle}>
            <Checkbox checked={hasOtherSelected} />
            <Label className="cursor-pointer text-sm">Other (please specify)</Label>
          </div>
          {hasOtherSelected && (
            <input
              type="text"
              value={otherText}
              onChange={(e) => handleOtherTextChange(e.target.value)}
              placeholder="Please specify..."
              className="ml-7 mt-2 px-3 py-2 border rounded-md text-sm"
              autoFocus
            />
          )}
        </div>
      </div>
    </div>
  );
};

export const AspirationSection = ({ responses, onSave, profile }) => {
  const getValue = (questionId) => responses?.[questionId];
  const managerName = profile?.manager_name || "[Manager Name]";
  const jobGrade = parseInt(profile?.job_grade) || 0;
  const showLeadershipSection = jobGrade >= 4;

  return (
    <div className="space-y-8">
      {/* Section intro */}
      <Card className="bg-[#f0f7ff] border-[#003366]/20">
        <CardContent className="pt-6">
          <p className="text-sm text-[#003366]">
            This section focuses on where you want to go – your career goals, development aspirations, 
            and how we can support your growth at Sagicor.
          </p>
        </CardContent>
      </Card>

      {/* 3.1 Career Goals */}
      <div>
        <h3 className="text-xl font-semibold text-[#003366] font-['Manrope'] mb-6">
          3.1 Career Goals
        </h3>

        <SingleChoiceQuestion
          id="3_1_q37"
          question="37. In the next 1-2 years, I see myself:"
          options={[
            "Growing deeper in my current technical role",
            "Moving to a more senior individual contributor role",
            "Transitioning into a team lead or management position",
            "Moving to a different technical specialization",
            "Moving to a business/non-technical role",
            "I'm not sure yet"
          ]}
          value={getValue("3_1_q37")}
          onChange={(val) => onSave("3_1_q37", val)}
        />

        <TextQuestion
          id="3_1_q38"
          question="38. Describe your ideal role in 3-5 years:"
          value={getValue("3_1_q38")}
          onChange={(val) => onSave("3_1_q38", val)}
          multiline
          maxLength={1500}
        />

        <TextQuestion
          id="3_1_q39"
          question="39. What specific skills or experiences do you need to achieve this goal?"
          value={getValue("3_1_q39")}
          onChange={(val) => onSave("3_1_q39", val)}
          multiline
          maxLength={1500}
        />
      </div>

      {/* 3.2 Development Planning */}
      <div>
        <h3 className="text-xl font-semibold text-[#003366] font-['Manrope'] mb-6">
          3.2 Development Planning
        </h3>

        <TextQuestion
          id="3_2_q40"
          question={`40. What development goals have you discussed with ${managerName}?`}
          value={getValue("3_2_q40")}
          onChange={(val) => onSave("3_2_q40", val)}
          multiline
          maxLength={1500}
        />

        <SingleChoiceQuestion
          id="3_2_q41"
          question={`41. How often do you discuss career development with ${managerName}?`}
          options={[
            "Every 1:1 meeting",
            "Monthly",
            "Quarterly",
            "During annual reviews only",
            "Rarely or never"
          ]}
          value={getValue("3_2_q41")}
          onChange={(val) => onSave("3_2_q41", val)}
        />
      </div>

      {/* 3.3 Skills Development */}
      <div>
        <h3 className="text-xl font-semibold text-[#003366] font-['Manrope'] mb-6">
          3.3 Skills Development
        </h3>

        <MultiChoiceQuestion
          id="3_3_q42"
          question="42. Which areas would you like to develop or strengthen? (Select all that apply)"
          options={[
            "Cloud Technologies (AWS, Azure, GCP)",
            "Security & Compliance",
            "DevOps & Automation",
            "AI / Machine Learning",
            "Data Analytics & BI",
            "Project Management",
            "Leadership & Management",
            "Communication & Presentation",
            "Business Acumen",
            "Vendor Management"
          ]}
          value={getValue("3_3_q42")}
          onChange={(val) => onSave("3_3_q42", val)}
        />

        <MultiChoiceQuestion
          id="3_3_q43"
          question="43. Are there any certifications you're interested in pursuing? (Select all that apply)"
          options={[
            "AWS Certifications",
            "Azure Certifications",
            "Google Cloud Certifications",
            "ITIL / ITSM Certifications",
            "Security Certifications (CISSP, Security+)",
            "Project Management (PMP, Prince2)",
            "Scrum / Agile Certifications",
            "Vendor-specific certifications",
            "Not interested in certifications currently"
          ]}
          value={getValue("3_3_q43")}
          onChange={(val) => onSave("3_3_q43", val)}
        />

        <SingleChoiceQuestion
          id="3_3_q44"
          question="44. What is your preferred learning style?"
          options={[
            "Self-paced online courses",
            "Instructor-led training",
            "Hands-on labs and practice",
            "Mentorship and job shadowing",
            "Reading books and documentation",
            "Conferences and workshops"
          ]}
          value={getValue("3_3_q44")}
          onChange={(val) => onSave("3_3_q44", val)}
        />
      </div>

      {/* 3.4 Knowledge Transfer */}
      <div>
        <h3 className="text-xl font-semibold text-[#003366] font-['Manrope'] mb-6">
          3.4 Knowledge Transfer
        </h3>

        <SingleChoiceQuestion
          id="3_4_q45"
          question="45. Are there systems or processes where you are the primary or only person with expertise?"
          options={[
            "Yes, I am the sole expert on critical systems",
            "Yes, for some non-critical systems",
            "No, knowledge is well-distributed",
            "I'm not sure"
          ]}
          value={getValue("3_4_q45")}
          onChange={(val) => onSave("3_4_q45", val)}
        />

        <TextQuestion
          id="3_4_q46"
          question="46. If yes, please list these systems or areas of expertise:"
          value={getValue("3_4_q46")}
          onChange={(val) => onSave("3_4_q46", val)}
          multiline
          maxLength={1500}
        />

        <SingleChoiceQuestion
          id="3_4_q47"
          question="47. Would you be interested in mentoring less experienced team members?"
          options={[
            "Yes, very interested",
            "Yes, if time permits",
            "Maybe, depending on the arrangement",
            "Not at this time"
          ]}
          value={getValue("3_4_q47")}
          onChange={(val) => onSave("3_4_q47", val)}
        />
      </div>

      {/* 3.5 Leadership & Influence (Grade 4+ only) */}
      {showLeadershipSection && (
        <div>
          <h3 className="text-xl font-semibold text-[#003366] font-['Manrope'] mb-6">
            3.5 Leadership & Influence
          </h3>
          <p className="text-sm text-muted-foreground mb-6">
            As a Grade {jobGrade} employee, please answer these leadership scenarios.
          </p>

          <TextQuestion
            id="3_5_q48"
            question="48. Your team's budget is being reduced by 20%, but output expectations remain the same. How would you approach this challenge?"
            value={getValue("3_5_q48")}
            onChange={(val) => onSave("3_5_q48", val)}
            multiline
            maxLength={2000}
          />

          <TextQuestion
            id="3_5_q49"
            question="49. You need buy-in from three different teams with competing priorities for a project. Describe your approach:"
            value={getValue("3_5_q49")}
            onChange={(val) => onSave("3_5_q49", val)}
            multiline
            maxLength={2000}
          />

          <TextQuestion
            id="3_5_q50"
            question="50. A senior engineer is resisting a mandated technology change. How would you handle this?"
            value={getValue("3_5_q50")}
            onChange={(val) => onSave("3_5_q50", val)}
            multiline
            maxLength={2000}
          />

          <TextQuestion
            id="3_5_q51"
            question="51. You realize a decision you made last quarter was wrong. How do you handle this?"
            value={getValue("3_5_q51")}
            onChange={(val) => onSave("3_5_q51", val)}
            multiline
            maxLength={2000}
          />

          <TextQuestion
            id="3_5_q52"
            question="52. A task would take you 2 hours but would take 2 days to teach a junior team member. How do you decide?"
            value={getValue("3_5_q52")}
            onChange={(val) => onSave("3_5_q52", val)}
            multiline
            maxLength={2000}
          />

          <TextQuestion
            id="3_5_q53"
            question="53. You're leading a project in a domain you're not familiar with. How do you approach this?"
            value={getValue("3_5_q53")}
            onChange={(val) => onSave("3_5_q53", val)}
            multiline
            maxLength={2000}
          />
        </div>
      )}

      {/* 3.6 Support Needs */}
      <div>
        <h3 className="text-xl font-semibold text-[#003366] font-['Manrope'] mb-6">
          3.6 Support Needs
        </h3>

        <TextQuestion
          id="3_6_q54"
          question={`54. What support would you like from ${managerName} that you're not currently receiving?`}
          value={getValue("3_6_q54")}
          onChange={(val) => onSave("3_6_q54", val)}
          multiline
          maxLength={1500}
        />

        <TextQuestion
          id="3_6_q55"
          question="55. What barriers, if any, are preventing you from doing your best work?"
          value={getValue("3_6_q55")}
          onChange={(val) => onSave("3_6_q55", val)}
          multiline
          maxLength={1500}
        />

        <TextQuestion
          id="3_6_q56"
          question="56. What would make you consider leaving Sagicor? (Please be candid – this helps us improve)"
          value={getValue("3_6_q56")}
          onChange={(val) => onSave("3_6_q56", val)}
          multiline
          maxLength={1500}
        />
      </div>

      {/* 3.7 Organizational Improvement */}
      <div>
        <h3 className="text-xl font-semibold text-[#003366] font-['Manrope'] mb-6">
          3.7 Organizational Improvement
        </h3>

        <TextQuestion
          id="3_7_q57"
          question="57. START: What should Sagicor Technology start doing?"
          value={getValue("3_7_q57")}
          onChange={(val) => onSave("3_7_q57", val)}
          multiline
          maxLength={1500}
          placeholder="Things we're not doing that would improve our team/organization..."
        />

        <TextQuestion
          id="3_7_q58"
          question="58. STOP: What should Sagicor Technology stop doing?"
          value={getValue("3_7_q58")}
          onChange={(val) => onSave("3_7_q58", val)}
          multiline
          maxLength={1500}
          placeholder="Things that don't add value or are counterproductive..."
        />

        <TextQuestion
          id="3_7_q59"
          question="59. CONTINUE: What should Sagicor Technology continue doing?"
          value={getValue("3_7_q59")}
          onChange={(val) => onSave("3_7_q59", val)}
          multiline
          maxLength={1500}
          placeholder="Things that are working well and should continue..."
        />

        <TextQuestion
          id="3_7_q60"
          question="60. Which of Sagicor's core values resonates most with you, and why?"
          value={getValue("3_7_q60")}
          onChange={(val) => onSave("3_7_q60", val)}
          multiline
          maxLength={1500}
        />
      </div>

      {/* 3.8 Final Reflections */}
      <div>
        <h3 className="text-xl font-semibold text-[#003366] font-['Manrope'] mb-6">
          3.8 Final Reflections
        </h3>

        <TextQuestion
          id="3_8_q61"
          question="61. What is the most valuable contribution you make to your team?"
          value={getValue("3_8_q61")}
          onChange={(val) => onSave("3_8_q61", val)}
          multiline
          maxLength={1500}
        />

        <TextQuestion
          id="3_8_q62"
          question="62. What would you want leadership to know that they might not?"
          value={getValue("3_8_q62")}
          onChange={(val) => onSave("3_8_q62", val)}
          multiline
          maxLength={1500}
        />

        <TextQuestion
          id="3_8_q63"
          question="63. What is your greatest hope for Sagicor Technology's future?"
          value={getValue("3_8_q63")}
          onChange={(val) => onSave("3_8_q63", val)}
          multiline
          maxLength={1500}
        />

        <TextQuestion
          id="3_8_q64"
          question="64. Is there anything else you'd like to share?"
          value={getValue("3_8_q64")}
          onChange={(val) => onSave("3_8_q64", val)}
          multiline
          maxLength={2000}
        />
      </div>
    </div>
  );
};

export default AspirationSection;
